import React from 'react';
import { Box } from '@mui/material';
import { indexBy, prop } from 'ramda';
import {
  PourReportDto,
  useMineModelRheologyDataListQuery,
  usePoursQuery
} from 'providers/api';
import { useScenario } from 'modules/project/providers/scenario';
import { computePourFlowRate } from 'modules/project/ProjectAreaPage/ProjectReportsPage/pourReportUtils';
import { buildRiseRateSeries } from 'modules/project/StopeFillPage/Three/riseRateSeries';
import { Model } from '../../hooks/useModelState';
import { SceneProps } from '../scene-utils';
import { Scene } from '../types';
import getStopeFilSceneActions from './StopeFillSceneActions';
import sideDrawerContentFunction from './StopeFillSceneSideDrawer';
import ModelDrawerTrigger from '../../ModelDrawerTrigger';
import { getStopeFillSceneState, subscribeToStopeFillSceneState } from './stopeFillSceneState';
import StopeVolumePanel from './StopeVolumePanel';
import RiseRatePanel from './RiseRatePanel';
import StopeFillLoadingOverlay from './StopeFillLoadingOverlay';

const TITLE = 'STOPE FILL VIEW';

interface StopeFillDisplayProps {
  props: SceneProps;
  model: Model;
}

const StopeFillDisplay = ({ props, model }: StopeFillDisplayProps) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  const { drawerOpen } = props.context.state;
  const stopeId = model.selectedStope?.data?.stopeId;
  const projectId = model.project?.entityId;
  const geometryPath = model.selectedStope?.data?.stopeGeometryFile?.path;

  const sceneState = getStopeFillSceneState();
  const { volumeData } = sceneState;
  const isStopeLoading = !!geometryPath && sceneState.readyGeometryPath !== geometryPath;

  const { activeScenarioId } = useScenario();
  const fillType = model.project?.fillType;

  const { data: poursData } = usePoursQuery(
    { projectId: projectId!, stopeId: stopeId!, scenarioId: activeScenarioId ?? undefined, includeRockFill: true },
    { enabled: !!projectId && !!stopeId, staleTime: 120000000 },
  );

  const { data: rheologyList } = useMineModelRheologyDataListQuery(
    { mineModelId: projectId!, rheologyDataSetIds: [] },
    { enabled: !!projectId, staleTime: 120000000 },
  );

  // Flow rate per pour, resolved once. The table's averaged rise rate and the sliced series below are
  // both drawn from this same number, so the graph can never disagree with the column beside it.
  const pourFlowRates = React.useMemo(() => {
    const rheologyById = indexBy(prop('rheologyDataSetId'), rheologyList ?? []);

    return new Map((poursData ?? []).map((sourcePour) => {
      const rheology = sourcePour.rheologyDataSetId ? rheologyById[sourcePour.rheologyDataSetId] : undefined;
      // Rock fill has no recipe, so there is no throughput to read a flow rate from.
      const flowRate = !sourcePour.isRockFill && fillType !== undefined
        ? computePourFlowRate(sourcePour as unknown as PourReportDto, rheology, fillType)
        : undefined;
      return [sourcePour.entityId, flowRate] as [string, number | undefined];
    }));
  }, [poursData, rheologyList, fillType]);

  const panelPours = React.useMemo(() => {
    if (!volumeData) return [];

    return volumeData.pours.map((pour) => {
      const flowRate = pourFlowRates.get(pour.entityId);
      // The averaged figure the table has always shown: the whole rise over the whole duration.
      const durationHours = flowRate !== undefined && flowRate > 0 ? pour.volume / flowRate : undefined;
      const riseRate = pour.height && durationHours && durationHours > 0
        ? pour.height / durationHours
        : undefined;
      return { ...pour, riseRate };
    });
  }, [volumeData, pourFlowRates]);

  // Sliced rise rate over the fill sequence. Stored volumes, not recomputed ones: the duration a pour
  // takes is set by what it is planned to place, which is the same figure the table's rise rate uses.
  const riseRateSeries = React.useMemo(() => {
    const stope = sceneState.originalStopeModel;
    if (!stope || !volumeData || volumeData.pours.length === 0) return undefined;

    return buildRiseRateSeries(
      stope,
      volumeData.pours.map((pour) => ({
        entityId: pour.entityId,
        volume: pour.volume,
        flowRate: pourFlowRates.get(pour.entityId),
      })),
      // Plot from the discharge floor, the datum the rest of the fill view measures heights from. A
      // drawpoint lobe below it is still filled, and still costs time, but its near-zero cross-section
      // would otherwise spike the rate axis and flatten the rest of the fill - see riseRateSeries.
      { plotFromLevel: sceneState.dischargeFloorLevel },
    );
  }, [sceneState.originalStopeModel, sceneState.dischargeFloorLevel, volumeData, pourFlowRates]);

  const pourNames = React.useMemo(
    () => new Map((volumeData?.pours ?? []).map((pour) => [pour.entityId, pour.name])),
    [volumeData],
  );

  React.useEffect(() => {
    const unsubscribe = subscribeToStopeFillSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (stopeId && projectId && geometryPath) {
      model.setStopeFillState({
        stopeId,
        projectId,
        geometryPath,
        drawerOpen,
        projectBeachAngleDegrees: model.project?.defaultPasteBeachAngleDegrees ?? null,
      });
    }
    return () => {
      model.setStopeFillState(null);
    };
  }, [stopeId, projectId, geometryPath, drawerOpen, model.project?.defaultPasteBeachAngleDegrees]);

  return (
    <>
      {!props.context.state.drawerOpen && (
        <Box sx={{ position: 'absolute', right: 2, zIndex: 20 }}>
          <ModelDrawerTrigger handleClick={() => props.context.actions.setDrawerOpen(true)} />
        </Box>
      )}
      {volumeData && (
        <StopeVolumePanel
          totalVolume={volumeData.totalVolume}
          remainingVolume={volumeData.remainingVolume}
          pours={panelPours}
          dimensions={volumeData.dimensions}
          drawerOpen={props.context.state.drawerOpen}
          unitSystemPreference={model.project?.unitSystemPreference}
          customUnitFields={model.project?.customUnitFields}
        />
      )}
      {riseRateSeries && (
        <RiseRatePanel
          series={riseRateSeries}
          pourNames={pourNames}
          drawerOpen={props.context.state.drawerOpen}
          unitSystemPreference={model.project?.unitSystemPreference}
          customUnitFields={model.project?.customUnitFields}
        />
      )}
      {isStopeLoading && <StopeFillLoadingOverlay drawerOpen={drawerOpen} />}
    </>
  );
};

const stopeFillScene = (props: SceneProps): Scene => {
  const display = (model: Model) => <StopeFillDisplay props={props} model={model} />;

  const getCanvasHandlers = () => ({
    onNodeHover: () => { },
    onNodeSelect: () => { },
    onSpoolSelect: () => { },
    onIntersections: () => { },
  });

  const sideDrawerContent = (model: Model) => (
    sideDrawerContentFunction(props, model)
  );

  return {
    key: props.key,
    display,
    getCanvasHandlers,
    sideDrawerContent,
    title: TITLE,
    actionButtons: getStopeFilSceneActions(),
  };
};

export default stopeFillScene;
